import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { Card, Dropdown } from "react-bootstrap";
import Toggle from "app/components/Toggle";
import { useEffect, useMemo, useState } from "react";
import TurnOffAutomaticTeamsModal from "./Modals/TurnOffAutomaticTeamsModal";
import TurnOnAutomaticTeamsModal from "./Modals/TurnOnAutomaticTeamsModal";
import {
  getAutomaticTeamsSettings,
  turnAutomaticTeamsSettingOn,
  turnAutomaticTeamsSettingOff,
  updateAutomaticTeamsEditSetting,
  selectHasAutomaticTeamsEnabled,
  selectGetAutomaticTeamsSettingsStatus,
  selectAutomaticTeamsEditSetting,
  selectTurnOffAutomaticTeamsSettingStatus,
} from "./slice";
import Loading from "app/storybookComponents/Loading";
import {
  getAllTeamsByCompanyAccountId,
  selectAllCompanyUsersById,
  selectCompanyInfo,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import GeneratedLogo from "resources/icons/generated-logo.svg";
import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SimplePopover from "app/components/Popovers/SimplePopover";
import { getPercentageOfCompanyWithManualTeams } from "./helpers";
import { trackAutomaticTeamsFeatureToggled } from "utils/trackingFunctions";

export default function AutomationSettings() {
  const dispatch = useAppDispatch();

  // ------------------------ Selectors ------------------------------------
  const getAutomaticTeamsSettingsStatus = useAppSelector(
    selectGetAutomaticTeamsSettingsStatus
  );
  const hasAutomaticTeamsEnabled = useAppSelector(
    selectHasAutomaticTeamsEnabled
  );
  const automaticTeamsEditSetting = useAppSelector(
    selectAutomaticTeamsEditSetting
  );
  const turnOffAutomaticTeamsSettingStatus = useAppSelector(
    selectTurnOffAutomaticTeamsSettingStatus
  );
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const companyInformation = useAppSelector(selectCompanyInfo);

  // ------------------------ Use Effects ------------------------------------

  useEffect(() => {
    if (getAutomaticTeamsSettingsStatus === "idle") {
      dispatch(getAutomaticTeamsSettings());
    }
  }, [getAutomaticTeamsSettingsStatus, dispatch]);
  const [showModal, setShowModal] = useState(false);

  const percentageOfCompanyWithManualTeams = useMemo(
    () => getPercentageOfCompanyWithManualTeams(usersById, teamsById),
    [usersById, teamsById]
  );

  if (getAutomaticTeamsSettingsStatus === "loading") {
    return <Loading />;
  }

  if (hasAutomaticTeamsEnabled === null) {
    return null;
  }

  const getModal = () => {
    if (!showModal) return null;
    if (hasAutomaticTeamsEnabled) {
      return (
        <TurnOffAutomaticTeamsModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onSave={(deleteAutomaticTeams: boolean) => {
            dispatch(
              turnAutomaticTeamsSettingOff({
                deleteAutomaticTeams,
              })
            );
            trackAutomaticTeamsFeatureToggled(false);
            if (companyInformation) {
              dispatch(
                getAllTeamsByCompanyAccountId({
                  companyAccountId: companyInformation.companyAccountId,
                })
              );
            }
          }}
          turnOffAutomaticTeamsSettingStatus={
            turnOffAutomaticTeamsSettingStatus
          }
        />
      );
    }
    return (
      <TurnOnAutomaticTeamsModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={() => {
          dispatch(turnAutomaticTeamsSettingOn());
          trackAutomaticTeamsFeatureToggled(true);
        }}
      />
    );
  };

  const getDropdownText = () => {
    if (automaticTeamsEditSetting === 1) {
      return "Only Admins";
    }
    if (automaticTeamsEditSetting === 2) {
      return "Admins and Team Leaders";
    }
    return "No One";
  };

  const renderAutomaticTeamsIconWithPopover = () => {
    return (
      <SimplePopover
        popoverContent={
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <p>
              Teams will be generated and updated automatically based on
              manager-employee relationships. Managers will be automatically
              assigned as team leader. Generated teams are indicated with a{" "}
              <img
                src={GeneratedLogo}
                alt="Generated Logo"
                className="inline-block"
              />{" "}
              icon. This makes HRIS integration and managing organizations at
              scale simpler.
            </p>
            {percentageOfCompanyWithManualTeams > 0 && (
              <>
                <p>
                  {percentageOfCompanyWithManualTeams}% of your organization is
                  configured with manual teams.
                </p>
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mt-4 text-amber-800 flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <p>
                    Turning on automatic teams now may lead to duplicate
                    teams... why? When automatic teams are on, they will
                    continually be created based on manager - direct report
                    relationships.
                  </p>
                </div>
              </>
            )}
          </div>
        }
        trigger="hover"
        popoverTitle="Automatic Teams"
        popoverClassName="!max-w-[400px]"
      >
        <img
          src={GeneratedLogo}
          alt="Generated Logo"
          className="inline-block"
        />
      </SimplePopover>
    );
  };

  return (
    <>
      {getModal()}
      <Card>
        <h2>Automated Teams</h2>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Automatically Create and Update Teams?</h3>
            <div>
              <p>
                Teams will be generated and updated automatically based on
                manager-employee relationships. Managers will be automatically
                assigned as team leader. Automatic teams are indicated with a{" "}
                {renderAutomaticTeamsIconWithPopover()}
                icon.
              </p>
            </div>
          </div>

          <div className="setting-row-action">
            <Toggle
              isOn={hasAutomaticTeamsEnabled}
              handleToggle={() => {
                setShowModal(true);
              }}
            />
          </div>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Who Can Edit Team Information For Automatic Teams?</h3>
            <p>
              Specify which users can edit team name, department, and team
              visibility for an Automatic team.
            </p>
          </div>
          <div className="setting-row-action">
            <Dropdown
              onSelect={(eventKey) => {
                if (!eventKey) return;
                dispatch(
                  updateAutomaticTeamsEditSetting(
                    parseInt(eventKey, 10) as 1 | 2 | 3
                  )
                );
              }}
            >
              <Dropdown.Toggle variant="light" id="dropdown-basic">
                {getDropdownText()}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="1">
                  <div className="d-flex align-items-center">Only Admins</div>
                </Dropdown.Item>
                <Dropdown.Item eventKey="2">
                  <div className="d-flex align-items-center">
                    Admins and Team Leaders
                  </div>
                </Dropdown.Item>
                <Dropdown.Item eventKey="3">
                  <div className="d-flex align-items-center">No One</div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Card>
    </>
  );
}
